import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Glow } from '@byaft/ui';

// Guard so a genuinely-broken chunk can't trigger an infinite reload loop: we
// auto-reload at most once per cooldown window, then fall back to the error UI.
//
// This is deliberately a *timestamp*, not a "have we reloaded yet?" boolean. The
// boundary always mounts cleanly first (the Suspense fallback renders fine) and only
// then does the chunk fetch reject — so any flag cleared on a successful mount is
// already gone by the time the error arrives, and the guard degrades into a loop.
const RELOAD_FLAG = 'byaft:chunk-reload';
const RELOAD_COOLDOWN_MS = 10_000;

// Self-contained pill buttons (web has no shared Button) — mirrors CookieConsent.
const buttonBase =
  'inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-label-bold ' +
  'font-display font-bold tracking-wide transition-all duration-300 active:scale-95';
const primaryButton =
  `${buttonBase} bg-primary-container text-on-primary-container shadow-glow-sm ` +
  'hover:shadow-glow-orange hover:scale-[1.04]';
const ghostButton =
  `${buttonBase} border border-glass-border bg-white/5 text-on-background ` +
  'hover:border-primary-container/60 hover:bg-white/[0.08] hover:scale-[1.03]';

/**
 * A failed lazy `import()` surfaces as one of these. The common cause here is a
 * *stale deploy*: a tab opened before a deploy still holds the old `index.html`, so
 * its chunk hashes no longer exist. `public/_redirects` ends in `/* /index.html 200`,
 * which catches `/assets/*` too — so the missing chunk comes back as `200 text/html`
 * and the browser refuses to parse it as a module. Either way, one reload fixes it.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.name === 'ChunkLoadError' ||
    /loading chunk [\d]+ failed/i.test(error.message) ||
    /failed to fetch dynamically imported module/i.test(error.message) ||
    /error loading dynamically imported module/i.test(error.message) ||
    /expected a javascript(-or-wasm)? module script/i.test(error.message)
  );
}

function reloadedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_FLAG));
    return Number.isFinite(at) && at > 0 && Date.now() - at < RELOAD_COOLDOWN_MS;
  } catch {
    // Storage unavailable — assume we already tried, so we show the fallback
    // rather than risk reloading forever.
    return true;
  }
}
function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    /* no-op — reloadedRecently() already fails safe */
  }
}

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
}

class ErrorBoundaryInner extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (isChunkLoadError(error) && !reloadedRecently()) {
      markReloaded();
      window.location.reload();
      return;
    }
    if (import.meta.env.DEV) console.error('ErrorBoundary caught:', error, info);
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <section className="relative flex min-h-[70svh] items-center overflow-hidden px-margin-safe py-stack-xl">
        <Glow className="left-1/2 top-1/2 h-[60vw] max-h-[480px] w-[60vw] max-w-[480px] -translate-x-1/2 -translate-y-1/2 blur-[120px]" />

        <div className="container-byaft text-center">
          <p className="font-display text-sm font-bold uppercase tracking-[0.28em] text-primary-container">
            Something went off-script
          </p>
          <h1 className="mt-4 text-headline-xl text-on-background">This part didn&apos;t load.</h1>
          <p className="mx-auto mt-5 max-w-xl text-body-lg text-on-surface-variant">
            An unexpected error interrupted this page. Reloading usually fixes it.
          </p>

          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button type="button" onClick={() => window.location.reload()} className={primaryButton}>
              Reload page
            </button>
            <Link to="/" className={ghostButton}>
              Back home
            </Link>
          </div>
        </div>
      </section>
    );
  }
}

/**
 * Catches render-time crashes and failed lazy-chunk loads in the routed content,
 * showing a branded fallback instead of a blank screen. Without this, a rejected
 * `lazy()` import unmounts the whole React root — the page goes fully black, header
 * and all, and only a manual reload brings it back.
 *
 * Keyed by the current path so it remounts (and resets) on navigation — a one-off
 * crash on one page never blanks the pages the user visits next.
 *
 * Note: React error boundaries only catch errors during render/lifecycle, not in
 * event handlers or async code.
 */
export function ErrorBoundary({ children }: Props) {
  const location = useLocation();
  return <ErrorBoundaryInner key={location.pathname}>{children}</ErrorBoundaryInner>;
}
